import React, { memo, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router';

import { Button, Stack, Typography } from '@mui/material';
import { NoAvailableMessage } from 'app/shared/components/common/NoAvailableMessage';
import withLoading, { WithLoadingPropTypes } from 'hocs/withLoading';
import { compose } from 'redux';

import { useEffectSafeStrictMode } from 'hooks/useEffectSafeStrictMode';
import useLoanId from 'hooks/useLoanId';

import { selectIsClaimAllowed } from 'store/auth/selectors';
import { openDialogAction } from 'store/global/actions';
import {
  clearLoanExceptionsAction,
  getLoanExceptionsForLoanAction,
} from 'store/loanExceptions/actions';
import { selectLoanExceptionsForLoan } from 'store/loanExceptions/selectors';
import { LoanExceptionDecision } from 'store/loanExceptions/types';
import { getExceptionSubCategoriesAction } from 'store/system/actions';
import { selectExceptionSubCategoriesList } from 'store/system/selectors';
import { getUsersAction } from 'store/users/actions';
import { selectExtendedUsers } from 'store/users/selectors';

import DecisionForm from './DecisionForm';
import ExceptionForm from './ExceptionForm';
import { APPROVE_CLAIM, VIEW_CLAIM } from './Exceptions.constants';
import { headerStackSx, rootStackSx } from './Exceptions.styles';
import { ExceptionsTestIds } from './Exceptions.testIds';
import { getSortedExceptions, getSubCategoryNameById } from './Exceptions.utils';
import ExceptionsTable from './ExceptionsTable';
import WithdrawForm from './WithdrawForm';

const Exceptions = ({ setIsLoading }) => {
  const dispatch = useDispatch();
  const loanId = useLoanId();
  const { applicationId } = useParams();

  const exceptions = useSelector(selectLoanExceptionsForLoan(loanId));
  const canView = useSelector(selectIsClaimAllowed(VIEW_CLAIM));
  const canApprove = useSelector(selectIsClaimAllowed(APPROVE_CLAIM));
  const subCategories = useSelector(selectExceptionSubCategoriesList);
  const usersById = useSelector(selectExtendedUsers);

  const subCategoryNameById = useMemo(() => getSubCategoryNameById(subCategories), [subCategories]);

  const sortedExceptions = useMemo(
    () => getSortedExceptions(exceptions, subCategoryNameById, usersById),
    [exceptions, subCategoryNameById, usersById],
  );

  const openCreate = useCallback(() => {
    dispatch(
      openDialogAction({
        header: 'Log new exception',
        body: () => (
          <ExceptionForm
            applicationId={applicationId}
            loanId={loanId}
            subCategories={subCategories}
          />
        ),
      }),
    );
  }, [applicationId, dispatch, loanId, subCategories]);

  const openEdit = useCallback(
    exception => {
      dispatch(
        openDialogAction({
          header: 'Edit exception',
          body: () => (
            <ExceptionForm
              applicationId={applicationId}
              exception={exception}
              loanId={loanId}
              subCategories={subCategories}
            />
          ),
        }),
      );
    },
    [applicationId, dispatch, loanId, subCategories],
  );

  const openApprove = useCallback(
    exception => {
      dispatch(
        openDialogAction({
          header: 'Approve exception',
          body: () => (
            <DecisionForm decision={LoanExceptionDecision.APPROVED} exception={exception} />
          ),
        }),
      );
    },
    [dispatch],
  );

  const openDeny = useCallback(
    exception => {
      dispatch(
        openDialogAction({
          header: 'Deny exception',
          body: () => (
            <DecisionForm decision={LoanExceptionDecision.DENIED} exception={exception} />
          ),
        }),
      );
    },
    [dispatch],
  );

  const openWithdraw = useCallback(
    exception => {
      dispatch(
        openDialogAction({
          header: 'Withdraw exception',
          body: () => <WithdrawForm exception={exception} />,
        }),
      );
    },
    [dispatch],
  );

  useEffectSafeStrictMode(() => {
    if (!canView) {
      return;
    }

    dispatch(getExceptionSubCategoriesAction()).catch(Error);
    dispatch(getUsersAction({ includeDeleted: true })).catch(Error);

    if (!loanId) {
      return;
    }

    setIsLoading(true);

    const controller = new AbortController();

    dispatch(getLoanExceptionsForLoanAction({ loanId, signal: controller.signal }))
      .catch(Error)
      .finally(() => setIsLoading(false));

    return () => {
      controller.abort();
      dispatch(clearLoanExceptionsAction());
    };
  }, [canView, dispatch, loanId, setIsLoading]);

  if (!canView) {
    return <NoAvailableMessage message="You do not have permission to view loan exceptions" />;
  }

  return (
    <Stack sx={rootStackSx}>
      <Stack direction="row" sx={headerStackSx}>
        <Typography variant="bold20">Exceptions</Typography>
        <Button data-testid={ExceptionsTestIds.ADD_BUTTON} variant="contained" onClick={openCreate}>
          Log exception
        </Button>
      </Stack>

      {sortedExceptions.length === 0 ? (
        <NoAvailableMessage message="No exceptions have been logged for this loan" />
      ) : (
        <ExceptionsTable
          canEdit
          canApprove={canApprove}
          exceptions={sortedExceptions}
          onApprove={openApprove}
          onDeny={openDeny}
          onEdit={openEdit}
          onWithdraw={openWithdraw}
        />
      )}
    </Stack>
  );
};

Exceptions.propTypes = {
  ...WithLoadingPropTypes,
};

export default compose(withLoading, memo)(Exceptions);
